import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    Typography,
    Collapse,
    Divider,
    List,
    ListItem,
    CircularProgress,
    Radio,
    RadioGroup,
    FormControlLabel,
    Stack,
    Chip,
} from "@mui/material";
import { fetchAvailableBundles, addBundleToCompany } from "../../services/selfServeEntitlements";

const AddEntitlementBundle = ({ companyId, subscription, entitlementDescriptions, onBundleAdded }) => {
    const [open, setOpen] = useState(false);
    const [availableBundles, setAvailableBundles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedBundles, setExpandedBundles] = useState({});
    const [selectedBundleId, setSelectedBundleId] = useState("");

    const handleOpen = () => {
        setOpen(true);
        setLoading(true);
        fetchAvailableBundles()
            .then((allBundles) => {
                const existingBundleIds = (subscription?.basedOn || []).map((b) => b._id);
                const filteredBundles = allBundles.filter((bundle) => !existingBundleIds.includes(bundle._id));
                setAvailableBundles(filteredBundles);
            })
            .catch((err) => console.error("Error fetching bundles:", err))
            .finally(() => setLoading(false));
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedBundleId("");
    };

    const handleToggle = (id) => {
        setExpandedBundles((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleAdd = () => {
        if (!selectedBundleId) return;
        const selectedBundle = availableBundles.find(b => b._id === selectedBundleId);
        const confirmation = window.confirm(
            `Are you sure you want to add the bundle "${selectedBundle?.title}" to this company?`
        );

        if (!confirmation) return; // ⛔ stop if user cancels
        addBundleToCompany(companyId, selectedBundleId)
            .then(() => {
                onBundleAdded();
                handleClose();
            })
            .catch((err) => console.error("Error adding bundle:", err));
    };

    return (
        <Box sx={{ mt: 4 }}>
            {/* Full-width primary action button */}
            <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={handleOpen}
                sx={{
                    py: 2,
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    boxShadow: 3,
                    "&:hover": { boxShadow: 6 },
                }}
            >
                Add Entitlement Bundle
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: "bold" }}>Select a Bundle to Add</DialogTitle>
                <DialogContent dividers>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : availableBundles.length === 0 ? (
                        <Typography sx={{ mt: 2 }} align="center" color="text.secondary">
                            No bundles available to add.
                        </Typography>
                    ) : (
                        <RadioGroup value={selectedBundleId} onChange={(e) => setSelectedBundleId(e.target.value)}>
                            <Stack spacing={2}>
                                {availableBundles.map((bundle) => (
                                    <Card key={bundle._id} variant="outlined" sx={{ p: 2 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <FormControlLabel
                                                value={bundle._id}
                                                control={<Radio />}
                                                label={
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {bundle.title.replace(/^GSD:/, '').trim()}
                                                    </Typography>
                                                }
                                            />
                                            <Button
                                                size="small"
                                                onClick={() => handleToggle(bundle._id)}
                                                sx={{ textTransform: "none" }}
                                            >
                                                {expandedBundles[bundle._id] ? "Hide Details" : "View Details"}
                                            </Button>
                                        </Box>

                                        <Collapse in={expandedBundles[bundle._id]} timeout="auto" unmountOnExit>
                                            <Divider sx={{ my: 1 }} />
                                            <List dense>
                                                {bundle.entitlements.map((ent) => {
                                                    const desc = entitlementDescriptions?.[ent.entitlement]?.description;

                                                    return (
                                                        <ListItem
                                                            key={ent.entitlement}
                                                            sx={{
                                                                pl: 0,
                                                                flexDirection: "column",
                                                                alignItems: "flex-start",
                                                                mb: 1,
                                                                backgroundColor: "#f9f9f9",
                                                                borderRadius: 1,
                                                                p: 1.5,
                                                            }}
                                                        >
                                                            {/* Entitlement Name */}
                                                            <Typography variant="body1" fontWeight="600">
                                                                {ent.entitlement}
                                                            </Typography>

                                                            {/* Description */}
                                                            {desc && (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ mt: 0.5, lineHeight: 1.4 }}
                                                                >
                                                                    {desc}
                                                                </Typography>
                                                            )}

                                                            {/* Feature / Quantity */}
                                                            {ent.type === "feature" ? (
                                                                <Chip
                                                                    label={ent.enabled ? "Enabled" : "Disabled"}
                                                                    size="small"
                                                                    color={ent.enabled ? "success" : "default"}
                                                                    sx={{ mt: 0.5 }}
                                                                />
                                                            ) : (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ mt: 0.5 }}
                                                                >
                                                                    Quantity: {ent.allocatedQuantity}
                                                                </Typography>
                                                            )}
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Collapse>


                                    </Card>
                                ))}
                            </Stack>
                        </RadioGroup>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleAdd}
                        variant="contained"
                        color="primary"
                        disabled={!selectedBundleId}
                    >
                        Add Bundle
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AddEntitlementBundle;
